import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { submitUserResponses, submitAssessmentOutcome, submitAbdominalResponses, submitPrediabetesResponses, submitIRResponses, updateAssessmentOutcome } from "../../../api/AssessmentHelper";
import Modal from "../../Shared/Modal";
import { Buffer } from "buffer";
import { getUserId, getAccesstoken } from "../../../utils/auth";

const StartMedicalAssessment = () => {
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assessmentAdded, setAssessmentAdded] = useState(false);
  const [showPreDiabetesQuestions, setShowPreDiabetesQuestions] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const userId = getUserId();

  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const access_token = getAccesstoken();
  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  const handleBack = () => {
    navigate(-1);
  };

  const searchParams = new URLSearchParams(location.search);
  const encodedData = searchParams.get("details");
 const mode = searchParams.get("mode");
  const { name: assessmentName } = JSON.parse(Buffer.from(decodeURIComponent(encodedData), "base64").toString("utf-8"));

  // Assessment questions based on type
  const assessmentQuestions = {
    "7a54fd47-a449-46fc-8fbc-a5e12d2b7c6f": [
      // Abdominal Obesity questions
      {
        id: "ao_q1",
        question_text: "Height (in cm)?",
        question_type: "text_input",
      },
      {
        id: "ao_q2",
        question_text: "Weight (kg)?",
        question_type: "text_input",
      },
      {
        id: "ao_q3",
        question_text: "Age?",
        question_type: "text_input",
      },
      {
        id: "ao_q4",
        question_text: "Body Mass Index (BMI)?",
        question_type: "text_input",
      },
      {
        id: "ao_q5",
        question_text: "Gender?",
        question_type: "radio",
        options: [
          { option_label: "Male", score: 0 },
          { option_label: "Female", score: 0 }
        ]
      },
      {
        id: "ao_q6",
        question_text: "Waist circumference (cm) ",
        question_type: "text_input",
      },
      {
        id: "ao_q7",
        question_text: "Waist to Height Ratio (WtH Ratio)",
        question_type: "text_input",
      },
      {
        id: "ao_q8",
        question_text: "Body Roundness Index",
        question_type: "text_input",
      },
    ],
    "4b2f9c68-df89-421a-9872-098e01b7a2ef": [
      // Prediabetes questions
      {
        id: "pd_q1",
        question_text: "Pre-diabetes risk",
        question_desc: "Have you taken the test?",
        question_type: "radio",
        options: [
          { option_label: "Yes", score: 0 },
          { option_label: "No", score: 0 }
        ]
      },
      {
        id: "pd_q2",
        question_text: "Serum Fasting Insulin (mIU/L)",
        question_type: "text_input",
      },
      {
        id: "pd_q3",
        question_text: "Fasting Blood glucose (mg/dl)",
        question_type: "text_input",
      },
      {
        id: "pd_q4",
        question_text: "HbA1c (%)",
        question_type: "text_input",
      },
      {
        id: "pd_q5",
        question_text: "Triglycerides",
        question_type: "text_input",
      },
      {
        id: "pd_q6",
        question_text: "HDL",
        question_type: "text_input",
      },
      {
        id: "pd_q7",
        question_text: "Do you have a parent or sibling with type 2 diabetes",
        question_type: "radio",
        options: [
          { option_label: "Yes", score: 0 },
          { option_label: "No", score: 0 }
        ]
      },
      {
        id: "pd_q8",
        question_text: "Are you physically inactive (< 3 times a week?)",
        question_type: "radio",
        options: [
          { option_label: "Yes", score: 0 },
          { option_label: "No", score: 0 }
        ]
      },
      {
        id: "pd_q9",
        question_text: "Gender?",
        question_type: "radio",
        options: [
          { option_label: "Male", score: 0 },
          { option_label: "Female", score: 0 }
        ]
      },
    ]
  };

  const questions = assessmentQuestions[params.assessmentId] || [];
  const questionsPerPage = 10;
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const currentQuestions = questions.slice(
    (currentPage - 1) * questionsPerPage,
    currentPage * questionsPerPage
  );

  const handleResponseChange = (questionId, value) => {
    // setResponses(prev => ({
    //   ...prev,
    //   [questionId]: value
    // }));
    if (questionId === "pd_q1") {
      setShowPreDiabetesQuestions(value === "Yes");
    }
    
    setResponses(prev => {
      const updatedResponses = { ...prev, [questionId]: value };
  
      // Extract necessary values
      const height = parseFloat(updatedResponses["ao_q1"]) / 100 || 0; // Convert cm to meters
      const weight = parseFloat(updatedResponses["ao_q2"]) || 0;
      const waistCircumference = parseFloat(updatedResponses["ao_q6"]) || 0;
  
      // Calculate BMI
      if (height > 0 && weight > 0) {
        updatedResponses["ao_q4"] = (weight / (height * height)).toFixed(2);
      }
  
      // Calculate Waist-to-Height Ratio
      if (height > 0 && waistCircumference > 0) {
        updatedResponses["ao_q7"] = (waistCircumference / (height * 100)).toFixed(2); // Height converted to cm
      }
  
      const waistInM = waistCircumference / 100; // Convert waist from cm to meters
      // Calculate BRI
      if (height > 0 && waistCircumference > 0) {
        updatedResponses["ao_q8"] = (
          364.2 - 365.5 * Math.sqrt(1 - (waistInM / (2 * Math.PI)) ** 2 / (0.5 * height) ** 2)
        ).toFixed(2);
      }
  
      return updatedResponses;
    });
  };

  const calculateScore = (assessmentId, responses) => {
    let totalScore = 0;
    
    if (assessmentName === 'Abdominal Obesity') {
      const waistCircumference = parseFloat(responses["ao_q6"]);
      const gender = responses["ao_q5"];
      const waisttoHeightRatio = parseFloat(responses["ao_q7"]);
      const BRI = parseFloat(responses["ao_q8"]);

      if (!isNaN(waistCircumference)) {
        if (gender === "Male" && waistCircumference > 94) {
          totalScore += 1;
        } else if (gender === "Female" && waistCircumference > 80) {
          totalScore += 1;
        }
      }
      if (!isNaN(waisttoHeightRatio) && waisttoHeightRatio > 0.5) {
        totalScore += 1;
      }
      if (!isNaN(BRI) && BRI > 7) {
        totalScore += 1;
      }
    } else if (assessmentName === 'Insulin Resistance and Pre-diabetes') {
      const SerumFastingInsulin = parseFloat(responses["pd_q2"]);
      const FastingBloodglucose = parseFloat(responses["pd_q3"]);
      const HbA1c = parseFloat(responses["pd_q4"]);

      if (!isNaN(SerumFastingInsulin) && SerumFastingInsulin > 15) {
        totalScore += 1;
      }
      if (!isNaN(FastingBloodglucose) && FastingBloodglucose > 110) {
        totalScore += 1;
      }
      if (!isNaN(HbA1c) && HbA1c > 5.7) {
        totalScore += 1;
      }
    }

    return totalScore;
  };

  const calculateIRScore = (prediabetesResponses) => {
    let irScore = 0;
    const {
      serum_fasting_insulin,
      fasting_blood_glucose,
      triglycerides,
      hdl,
      gender
    } = prediabetesResponses;

    // Calculate HOMA IR
    const homaIR = parseFloat((fasting_blood_glucose * serum_fasting_insulin) / 22.5).toFixed(2);
    if (homaIR > 1.85 && gender === 'Male') {
      irScore += 1;
    }

    if( homaIR > 2.07 && gender === 'Female'){
      irScore +=1;
    }
    // Calculate TyG Index
    const tygIndex = parseFloat(
      (triglycerides * fasting_blood_glucose) / 2
    ).toFixed(2);
    if (tygIndex > 4.65) {
      irScore += 1;
    }

    const gender1=''
    // Calculate Triglyceride HDL ratio
    const tgHdlRatio = parseFloat(triglycerides / hdl).toFixed(2);
    if ((gender === "Male" && tgHdlRatio > 3.4) || 
      (gender === "Female" && tgHdlRatio > 2.6)) {
      irScore += 1;
    }

    return irScore;
  };

  const handleSubmit = async () => {
    try {
      const createdAt = new Date().toISOString();
      const updatedAt = new Date().toISOString();
      const totalScore = calculateScore(params.assessmentId, responses);
      
      if (assessmentName === 'Abdominal Obesity') {
        const questionMapping = {
          ao_q1: "height",
          ao_q2: "weight",
          ao_q3: "age",
          ao_q4: "bmi",
          ao_q5: "gender",
          ao_q6: "waist_circumference",
          ao_q7: "waist_height_ratio",
          ao_q8: "bri"
        };

        const userAbdominalResponses = Object.entries(responses).reduce((acc, [questionId, answer]) => {
          const field = questionMapping[questionId];
          if (field) {
            acc[field] = isNaN(answer) ? answer : Number(answer);
          }
          return acc;
        }, {
          user_id: userId,
          height: 0,
          weight: 0,
          age: 0,
          bmi: 0,
          gender: "string",
          waist_circumference: 0,
          waist_height_ratio: 0,
          bri: 0,
          created_at: createdAt,
          created_by: userId
        });

        await submitAbdominalResponses(userAbdominalResponses, access_token);
      } else if (assessmentName === 'Insulin Resistance and Pre-diabetes') {
        const questionMapping = {
          pd_q1: "prediabetes_risk_score",
          pd_q2: "serum_fasting_insulin",
          pd_q3: "fasting_blood_glucose",
          pd_q4: "hba1c",
          pd_q5: "triglycerides",
          pd_q6: "hdl",
          pd_q7: "parent_sibling_diabetes",
          pd_q8: "physically_active",
          pd_q9: "gender"
        };

        const userPrediabetesResponses = Object.entries(responses).reduce((acc, [questionId, answer]) => {
          const field = questionMapping[questionId];
          if (field) {
            acc[field] = isNaN(answer) ? answer : Number(answer);
          }
          return acc;
        }, {
          user_id: userId,
          prediabetes_risk_score: '',
          serum_fasting_insulin: 0,
          fasting_blood_glucose: 0,
          hba1c: 0,
          triglycerides: 0,
          hdl: 0,
          parent_sibling_diabetes: '',
          physically_active: '',
          gender: '',
          created_at: createdAt,
          created_by: userId
        });

        await submitPrediabetesResponses(userPrediabetesResponses, access_token);

        // Calculate and submit Insulin Resistance assessment
        const irScore = calculateIRScore(userPrediabetesResponses);
        const insulinResistanceResponses = {
          user_id: userId,
          homa_ir: parseFloat((userPrediabetesResponses.fasting_blood_glucose * userPrediabetesResponses.serum_fasting_insulin) / 22.5).toFixed(2),
          tyg_index: Math.log(parseFloat((userPrediabetesResponses.triglycerides * userPrediabetesResponses.fasting_blood_glucose) / 2)).toFixed(2),
          hdl_ratio: parseFloat(userPrediabetesResponses.triglycerides / userPrediabetesResponses.hdl).toFixed(2),
          created_at: createdAt,
          created_by: userId
        };

       await submitIRResponses(insulinResistanceResponses, access_token);

        // Submit IR assessment outcome
        const irAssessmentOutcome = {
          user_id: userId,
          assessment_id: "cdf191f0-020d-45de-b9c9-a3bfa5610fcc", // You'll need to provide the correct ID
          total_score: irScore,
          status: "completed",
          created_at: createdAt
        };

        await submitAssessmentOutcome(irAssessmentOutcome, access_token);
      }

      const assessmentOutcome = {
        user_id: userId,
        assessment_id: params.assessmentId,
        total_score: totalScore,
        status: "completed",
        created_at: createdAt
      };

      const assessmentUpdateOutcome = {
        user_id: userId,
        assessment_id: params.assessmentId,
        total_score: totalScore,
        status: "completed",
        updated_at: updatedAt
      };

      if(mode === "edit"){
        await updateAssessmentOutcome(params.assessmentId, userId, assessmentUpdateOutcome, access_token)
      }else{
        await submitAssessmentOutcome(assessmentOutcome, access_token);
      }
      navigate(`/user/medical-assessment/responses/${params.assessmentId}?details=${encodedData}`);
      // setAssessmentAdded(true);
      // setTimeout(() => {
        
      //   navigate(`/user/medical-assessment/responses/${params.assessmentId}?details=${encodedData}`);
      // }, 2000);
    } catch (error) {
      setError("Failed to submit assessment");
      console.error(error);
    }
  };

  // Render questions and navigation
  return (
    <div className="dashboard_grey_section">
      <h2 className="font-lg mb-4">{assessmentName} Assessment</h2>
      {/* {assessmentName === 'Insulin Resistance and Pre-diabetes' && (
  <h3 style={{ marginBottom: 2 }}>
    We recommned to take the Pre-diabetes assessment first <Link to={'/calculators/'}>Click Here</Link>
  </h3>
)} */}

      {/* <div className="dashboard-user-name">
        <p>
Description not available
        </p>
      </div> */}
      {currentQuestions.length > 0 ? (
  currentQuestions.map((question) => {
    if (
      ["pd_q2", "pd_q3", "pd_q4", "pd_q5", "pd_q6", "pd_q7", "pd_q8", "pd_q9"].includes(question.id) &&
      !showPreDiabetesQuestions
    ) {
      return null;
    }
    return(
    <div key={question.id} className="assesment-cards mb-3">
      <div className="dashboard-title mb-3">
      {question.id === "pd_q2" && (
        <div className="font-smd fw-semibold"><p style={{ marginBottom: 2 }}>
          We highly recommend to opt for Screening for Prediabetes under <Link to={'/cart/'}>Add-on services. </Link> Reports will be automatically updated once screening for pre-diabetes is done. You may add values if done in last 3 months. 
        </p></div>
      )}
      </div>
      <div className="dashboard-title mb-3">
        <div className="font-smd fw-semibold">{question.question_text}</div>
        <p>{question.question_desc}</p>
      </div>
      {/* Render input based on question type */}
      {question.question_type === "radio" ? (
        question.options.map((option, index) => (
          <div key={index} className="form-check">
            <input
              type="radio"
              className="form-check-input"
              name={question.id}
              value={option.option_label}
              checked={responses[question.id] === option.option_label}
              onChange={(e) => handleResponseChange(question.id, e.target.value)}
              required
            />
            <label className="form-check-label">{option.option_label}</label>
          </div>
        ))
      ) : (
        <input
          type="text"
          className="form-control"
          value={responses[question.id] || ""}
          onChange={(e) => handleResponseChange(question.id, e.target.value)}
          readOnly={["ao_q4", "ao_q7", "ao_q8"].includes(question.id)}
          required
        />
      )}
      {question.id === "pd_q1" && responses[question.id] === "No" && (
        <div className="font-smd fw-semibold mt-2"><p style={{ marginBottom: 2 }}>
           We recommend to evaluate your risk for pre-diabetes. <Link to={'/calculators/'}>Click Here</Link>
        </p></div>
      )}
    </div>
    )
})
) : (
  <p className="text-center text-muted">
    No questions available for this assessment / It's a derived assessment.
  </p>
)}

      
      <div className="d-flex justify-content-between mt-4">
      {currentPage === 1 ? (
          <button
            onClick={handleBack}
            className="btn-assessment font-xsm btns btn-animation-blue mt-xxl-4 mt-2 home-button"
          >
            Back
          </button>
        ) : (
          <button
            onClick={handlePrev}
            className="btn-assessment font-xsm btns btn-animation-blue mt-xxl-4 mt-2 home-button"
          >
            Previous
          </button>
        )}
        {currentPage < totalPages ? (
          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            className="btn-assessment font-xsm btns btn-animation-blue mt-xxl-4 mt-2 home-button"
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="btn-assessment font-xsm btns btn-animation-blue mt-xxl-4 mt-2 home-button"
          >
            Submit
          </button>
        )}
      </div>
      
      {assessmentAdded && (
        <Modal
          title="Assessment Submitted!"
          modalType="success"
          bodyText="Records stored successfully.."
          modalId="loginModal"
        />
      )}
    </div>
  );
};

export default StartMedicalAssessment; 